import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Building2, 
  Globe, 
  Phone, 
  Mail, 
  MapPin, 
  Loader2, 
  CheckCircle,
  Upload
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface BusinessProfileEditorProps {
  merchantId: string;
}

export function BusinessProfileEditor({ merchantId }: BusinessProfileEditorProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [form, setForm] = useState({
    store_name: '',
    contact_person_name: '',
    business_email: '',
    business_phone: '',
    business_address: '',
    website_url: '',
    logo_url: '',
    gst_number: '',
    pan_number: '',
  });

  useEffect(() => {
    fetchProfile();
  }, [merchantId]);

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('merchants')
        .select('store_name, contact_person_name, business_email, business_phone, business_address, website_url, logo_url, gst_number, pan_number')
        .eq('id', merchantId)
        .single();
      
      if (error) throw error;
      if (data) {
        setForm({
          store_name: data.store_name || '',
          contact_person_name: data.contact_person_name || '',
          business_email: data.business_email || '',
          business_phone: data.business_phone || '',
          business_address: data.business_address || '',
          website_url: data.website_url || '',
          logo_url: data.logo_url || '',
          gst_number: data.gst_number || '',
          pan_number: data.pan_number || '',
        });
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be less than 2MB');
      return;
    }
    setUploadingLogo(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${merchantId}-logo.${ext}`;
      const { error } = await supabase.storage.from('profile-pictures').upload(fileName, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('profile-pictures').getPublicUrl(fileName);
      setForm(prev => ({ ...prev, logo_url: publicUrl }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('merchants')
        .update({
          store_name: form.store_name,
          contact_person_name: form.contact_person_name || null,
          business_email: form.business_email || null,
          business_phone: form.business_phone || null,
          business_address: form.business_address || null,
          website_url: form.website_url || null,
          logo_url: form.logo_url || null,
          gst_number: form.gst_number || null,
          pan_number: form.pan_number || null,
        })
        .eq('id', merchantId);
      if (error) throw error;
      toast.success('Business profile updated!');
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Business Profile</h2>
        <p className="text-sm text-muted-foreground">Manage your business details and branding</p>
      </div>

      {/* Logo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Logo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {form.logo_url ? (
              <img src={form.logo_url} alt="Logo" className="h-16 w-16 object-contain rounded border" />
            ) : (
              <div className="h-16 w-16 rounded border border-dashed flex items-center justify-center bg-muted/30">
                <Building2 className="h-6 w-6 text-muted-foreground" />
              </div>
            )}
            <div>
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="profile-logo-upload" />
              <Button variant="outline" size="sm" asChild disabled={uploadingLogo}>
                <label htmlFor="profile-logo-upload" className="cursor-pointer">
                  {uploadingLogo ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  Upload Logo
                </label>
              </Button>
              <p className="text-xs text-muted-foreground mt-1">PNG or JPG, max 2MB</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4 text-cyan-600" />
            Business Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Business / Practice Name</Label>
              <Input value={form.store_name} onChange={e => setForm(p => ({ ...p, store_name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Your Name</Label>
              <Input value={form.contact_person_name} onChange={e => setForm(p => ({ ...p, contact_person_name: e.target.value }))} placeholder="Your full name" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1"><Mail className="h-3 w-3" /> Email</Label>
              <Input type="email" value={form.business_email} onChange={e => setForm(p => ({ ...p, business_email: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</Label>
              <Input value={form.business_phone} onChange={e => setForm(p => ({ ...p, business_phone: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-1"><Globe className="h-3 w-3" /> Website</Label>
            <Input value={form.website_url} onChange={e => setForm(p => ({ ...p, website_url: e.target.value }))} placeholder="https://yourwebsite.com" />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Address</Label>
            <Textarea value={form.business_address} onChange={e => setForm(p => ({ ...p, business_address: e.target.value }))} rows={2} />
          </div>
        </CardContent>
      </Card>

      {/* Tax Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tax Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>GST Number</Label>
              <Input value={form.gst_number} onChange={e => setForm(p => ({ ...p, gst_number: e.target.value.toUpperCase() }))} placeholder="22AAAAA0000A1Z5" />
            </div>
            <div className="space-y-2">
              <Label>PAN Number</Label>
              <Input value={form.pan_number} onChange={e => setForm(p => ({ ...p, pan_number: e.target.value.toUpperCase() }))} placeholder="ABCDE1234F" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
        Save Changes
      </Button>
    </div>
  );
}
